import { useAxios } from '@/composables/use-axios'

const API_BASE_URL = import.meta.env.VITE_SERVER_API_SHOPIFY
const API_BASE_URL_V1_2 = `${import.meta.env.VITE_SERVER_API_SHOPIFY}/v1-2/`
const API_BASE_URL_V1_3 = `${import.meta.env.VITE_SERVER_API_SHOPIFY}/v1-3/`

interface ApiResponse<T> {
  code: number
  message: string
  data: T
}

interface ShopifyOfflineAuthResponse {
  code: number
  message?: string
  data?: {
    url?: string
  }
  url?: string
}

export interface ShopifyListResult<TItem> {
  total: number
  records: TItem[]
  page?: number
  page_size?: number
  [key: string]: unknown
}

export async function requestShopifyOfflineAuth(shopDomain: string): Promise<string> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ShopifyOfflineAuthResponse>(`${API_BASE_URL}/api/oauth/shopify-offline-auth`, {
    params: { shop: shopDomain },
    withCredentials: true,
  })

  const body = response.data
  if (body?.code !== 1)
    throw new Error(body?.message || 'Unable to start Shopify authorization flow.')

  const redirectUrl = body?.data?.url || body?.url
  if (!redirectUrl)
    throw new Error('Authorization URL is missing from server response.')

  return redirectUrl
}

export interface ShopifyListParams {
  page?: number
  page_size?: number
  ids?: string | number | Array<string | number>
  name?: string
}

export type ShopifyVariantsListParams = ShopifyListParams
export interface ShopifyProductListParams extends ShopifyListParams {
  status?: string
  [key: string]: unknown
}

export interface ShopifyItem {
  id: string | number
  variant_ids?: Array<string | number>
  product_name?: string
  name?: string
  title?: string
  variant_title?: string
  sku?: string
  image?: string
  featured_image?: string
  image_url?: string
  [key: string]: unknown
}

function normaliseIds(ids?: ShopifyListParams['ids']) {
  if (!ids)
    return undefined

  if (Array.isArray(ids))
    return ids.map(id => String(id)).filter(Boolean).join(',')

  return String(ids)
}

/**
 * 获取 Shopify 变体列表（支持根据名称或 ID 查询）
 */
export async function getShopifyVariantsList(params: ShopifyVariantsListParams): Promise<ShopifyListResult<ShopifyItem>> {
  const { axiosInstance } = useAxios()
  const { ids, page, page_size, ...rest } = params

  const response = await axiosInstance.get<ApiResponse<ShopifyListResult<ShopifyItem>>>(`${API_BASE_URL_V1_2}variants/list`, {
    params: {
      page: page ?? 1,
      page_size: page_size ?? 50,
      ...rest,
      ...(ids ? { ids: normaliseIds(ids) } : {}),
    },
  })

  return response.data.data
}

/**
 * 获取 Shopify 产品列表（支持分页、搜索、按 ID 批量查询）
 */
export async function getShopifyProductList(params: ShopifyProductListParams): Promise<ShopifyListResult<ShopifyItem>> {
  const { axiosInstance } = useAxios()
  const { ids, name, page, page_size, ...rest } = params

  const payload = {
    page: page ?? 1,
    page_size: page_size ?? 50,
    ...(name ? { name } : {}),
    ...(ids ? { ids: Array.isArray(ids) ? ids.map(String) : [String(ids)] } : {}),
    ...rest,
  }

  const response = await axiosInstance.post<ApiResponse<ShopifyListResult<ShopifyItem>>>(`${API_BASE_URL_V1_3}product/list`, payload)

  return response.data.data
}

export interface ShopifySegmentItem {
  id: string
  name: string
  query: string
  creation_date: string
  last_edit_date: string
}

export interface ShopifySegmentMemberItem {
  id: string
  display_name: string
  first_name: string
  last_name: string
  email: string
  phone: string
  number_of_orders: string
  amount_spent: number
  currency: string
  last_order_id: string
}

export interface ShopifySegmentListParams {
  name?: string
  page?: number
  page_size?: number
}

export interface ShopifySegmentMemberListParams {
  segment_id: string
  page?: number
  page_size?: number
}

/**
 * 获取 Shopify 客户分群列表（支持名称搜索、分页）
 */
export async function getShopifySegmentList(params: ShopifySegmentListParams = {}): Promise<ShopifyListResult<ShopifySegmentItem>> {
  const { axiosInstance } = useAxios()

  const response = await axiosInstance.post<ApiResponse<ShopifyListResult<ShopifySegmentItem>>>(`${API_BASE_URL_V1_3}shopify/segment/list`, {
    page: params.page ?? 1,
    page_size: params.page_size ?? 20,
    ...(params.name ? { name: params.name } : {}),
  })

  return response.data.data
}

/**
 * 获取指定 Shopify 客户分群下的成员列表（支持分页）
 */
export async function getShopifySegmentMemberList(params: ShopifySegmentMemberListParams): Promise<ShopifyListResult<ShopifySegmentMemberItem>> {
  const { axiosInstance } = useAxios()

  const response = await axiosInstance.post<ApiResponse<ShopifyListResult<ShopifySegmentMemberItem>>>(`${API_BASE_URL_V1_3}shopify/segment/member-list`, {
    segment_id: params.segment_id,
    page: params.page ?? 1,
    page_size: params.page_size ?? 20,
  })

  return response.data.data
}

export interface ShopifyCreateSubscriptionPayload {
  sub_items: string
  sub_pro?: Record<string, unknown>
  coupon_code?: string
  [key: string]: unknown
}

export interface ShopifyCreateSubscriptionData {
  pay_url?: string
  [key: string]: unknown
}

export async function createShopifySubscriptionLink(payload: ShopifyCreateSubscriptionPayload): Promise<ApiResponse<ShopifyCreateSubscriptionData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<ShopifyCreateSubscriptionData>>(`${API_BASE_URL_V1_3}bill/create-subscription-v5`, payload)

  return response.data
}

export interface ShopifyAppEmbedStatus {
  enable: boolean
  url?: string
  enabled_at?: string
}

export async function getShopifyThemeAppEmbedStatus(): Promise<ApiResponse<ShopifyAppEmbedStatus>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<ShopifyAppEmbedStatus>>(`${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-1/attribuly/check-app-embed`)
  return response.data
}

export async function getShopifyAppEmbedEnableUrl(): Promise<ApiResponse<{ url: string }>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<{ url: string }>>(`${API_BASE_URL_V1_2}theme/app-embed/enable-url`)
  return response.data
}

export interface ShopifyWriteCustomersCheckData {
  is_shopify: boolean
  has_write_customers: boolean
  shop_id?: number | string
  shop_domain?: string
  scopes?: string[]
  reauth_url?: string
}

/**
 * 检查当前店铺是否为 Shopify 且具备 write_customers 权限
 * GET /v1-2/shops/check-write-customers
 */
export async function checkShopifyWriteCustomers(): Promise<ApiResponse<ShopifyWriteCustomersCheckData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<ShopifyWriteCustomersCheckData>>(`${API_BASE_URL_V1_2}shops/check-write-customers`)
  return response.data
}

export interface ShoplineWriteCustomersCheckData {
  is_shopline: boolean
  has_write_customers: boolean
  shop_id?: number | string
  shop_domain?: string
  scopes?: string[]
  reauth_url?: string
}

/**
 * 检查当前店铺是否为 Shopline 且具备 write_customers 权限
 * GET /v1-2/shops/check-write-customers-shopline
 */
export async function checkShoplineWriteCustomers(): Promise<ApiResponse<ShoplineWriteCustomersCheckData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<ShoplineWriteCustomersCheckData>>(`${API_BASE_URL_V1_2}shops/check-write-customers-shopline`)
  return response.data
}

export interface ShopifyCaptureInfoRecord {
  [key: string]: unknown
}

export interface ShopifyCaptureInfoData {
  emails_identified?: number
  revenue_generated?: number
  cost?: number
  roi?: number
  records?: ShopifyCaptureInfoRecord[]
}

export async function getShopifyCaptureInfo(): Promise<ApiResponse<ShopifyCaptureInfoData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<ShopifyCaptureInfoData>>(`${API_BASE_URL_V1_3}capture/info`)
  return response.data
}

// Event Billing Types
export interface EventBillingItem {
  event_type: string
  event_count: number
  credit_usage: number
  cost: number
  revenue_generated: number
  roi: number
}

export interface CurrentBillingData {
  start_at: string
  end_at: string
  list: EventBillingItem[]
}

export interface BillingHistoryRecord {
  id: number
  bill_id: number
  bill_date: string
  start_at: string
  end_at: string
  plan: string
  billing_type: string
  bill_cost: number
  credit_usage: number
  event_count: number
  revenue_generated: number
  roi: number
}

export interface BillingHistoryListData {
  total: number
  Records: BillingHistoryRecord[]
}

export interface BillingItemDetailData {
  Records: EventBillingItem[]
}

export interface GetCurrentBillingParams {
  timezone: string
  date_start?: string
  date_end?: string
}

export interface GetBillingHistoryListParams {
  page?: number
  page_size?: number
}

export interface GetBillingItemParams {
  id: number
}

/**
 * 获取当前账单（基于事件计费）
 * GET /v1-3/event-bill/current-item
 */
export async function getCurrentBillingItem(params: GetCurrentBillingParams): Promise<ApiResponse<CurrentBillingData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<CurrentBillingData>>(`${API_BASE_URL_V1_3}event-bill/current-item`, {
    params,
  })

  return response.data
}

/**
 * 获取历史账单列表
 * GET /v1-3/event-bill/items
 */
export async function getBillingHistoryList(params?: GetBillingHistoryListParams): Promise<ApiResponse<BillingHistoryListData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<BillingHistoryListData>>(`${API_BASE_URL_V1_3}event-bill/items`, {
    params: {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 20,
    },
  })

  return response.data
}

/**
 * 获取历史账单详情
 * GET /v1-3/event-bill/item
 */
export async function getBillingHistoryItem(params: GetBillingItemParams): Promise<ApiResponse<BillingItemDetailData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<BillingItemDetailData>>(`${API_BASE_URL_V1_3}event-bill/item`, {
    params,
  })

  return response.data
}

export interface RecaptureSettlement {
  subscription_id: number
  settlement_key: string
  settlement_type: string
  status: string
  blocked_reason: string
  period_start: string
  period_end: string
  recovered_revenue: number
  gmv_12m: number
  max_monthly_applied: number
  base_charge: number
  carry_in: number
  charged_amount: number
  raw_due: number
  actual_due: number
  carry_out: number
  credit_amount: number
}

export interface RecaptureBillCurrentData {
  period_start?: string
  period_end?: string
  credit_balance: number
  item: RecaptureSettlement | null
}

export interface RecaptureBillListData {
  total: number
  records: RecaptureSettlement[]
}

export async function getRecaptureBillCurrentItem(): Promise<ApiResponse<RecaptureBillCurrentData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<RecaptureBillCurrentData>>(`${API_BASE_URL_V1_3}recapture-bill/current-item`)
  return response.data
}

export async function getRecaptureBillItems(params?: GetBillingHistoryListParams): Promise<ApiResponse<RecaptureBillListData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<RecaptureBillListData>>(`${API_BASE_URL_V1_3}recapture-bill/items`, {
    params: {
      page: params?.page ?? 1,
      page_size: params?.page_size ?? 20,
    },
  })

  return response.data
}

export interface RecaptureFreeData {
  [key: string]: unknown
}

/**
 * 激活 Recapture Free 套餐
 * GET /v1-3/bill/recapture-free
 */
export async function recaptureFreeApi(): Promise<ApiResponse<RecaptureFreeData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<RecaptureFreeData>>(`${API_BASE_URL_V1_3}bill/recapture-free`)
  return response.data
}

export interface CreateCaptureSubscriptionPayload {
  plan_name: string
}

export interface CreateCaptureSubscriptionData {
  [key: string]: unknown
}

/**
 * 创建 Capture 订阅
 * POST /v1-3/bill/create-subscription-capture
 */
export async function createCaptureSubscription(payload: CreateCaptureSubscriptionPayload): Promise<ApiResponse<CreateCaptureSubscriptionData>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<CreateCaptureSubscriptionData>>(`${API_BASE_URL_V1_3}bill/create-subscription-capture`, payload)
  return response.data
}
